// Sky Blue Pathways registration form.
// Collects guardians + N participants, posts the data to the app proxy
// (/apps/pathways/register), then adds one cart line per participant carrying
// the returned submission reference as a line item property.

class PathwaysRegistration extends HTMLElement {
  connectedCallback() {
    this.config = this.#readConfig();
    this.form = this.querySelector('.pathways__form');
    this.participantsEl = this.querySelector('.pathways__participants');
    this.template = this.querySelector('[ref="participantTemplate"]');
    this.addButton = this.querySelector('.pathways__add');
    this.submitButton = this.querySelector('.pathways__submit');
    this.errorEl = this.querySelector('.pathways__error');
    this.totalAmountEl = this.querySelector('[ref="totalAmount"]');

    if (!this.form || !this.template) return;

    this.addButton?.addEventListener('click', () => this.#addParticipant());
    this.form.addEventListener('submit', (event) => this.#handleSubmit(event));

    this.#addParticipant();
  }

  #readConfig() {
    const el = this.querySelector('[data-pathways-config]');
    try {
      return JSON.parse(el?.textContent ?? '{}');
    } catch {
      return {};
    }
  }

  #addParticipant() {
    const fragment = this.template.content.cloneNode(true);
    const row = fragment.querySelector('[data-participant]');

    const removeButton = row.querySelector('[data-remove]');
    removeButton?.addEventListener('click', () => {
      row.remove();
      this.#renumber();
      this.#updateTotal();
    });

    const typeSelect = row.querySelector('[data-toggle-kit]');
    if (typeSelect) {
      typeSelect.addEventListener('change', () => {
        this.#toggleKit(row);
        this.#updateTotal();
      });
    }

    this.participantsEl.appendChild(row);
    this.#decorateKitOptions(row);
    this.#toggleKit(row);
    this.#renumber();
    this.#updateTotal();
  }

  #renumber() {
    const rows = [...this.participantsEl.querySelectorAll('[data-participant]')];
    rows.forEach((row, i) => {
      const title = row.querySelector('[data-title]');
      if (title) title.textContent = `Participant ${i + 1}`;
      const removeButton = row.querySelector('[data-remove]');
      if (removeButton) removeButton.hidden = rows.length === 1;
    });
  }

  // Shows the kit fields and marks them required only when "With Kit" is
  // selected; otherwise hides the whole group and clears its values.
  #toggleKit(row) {
    const withKit = this.#isWithKit(row);
    const group = row.querySelector('[data-kit-group]');
    if (!group) return;
    group.hidden = !withKit;
    group.querySelectorAll('select, input').forEach((control) => {
      control.required = withKit;
      if (!withKit) control.value = '';
    });
  }

  #isWithKit(row) {
    if (!this.config.hasKitChoice) return false;
    const typeSelect = row.querySelector('[data-toggle-kit]');
    return typeSelect ? typeSelect.value === 'withKit' : false;
  }

  // Resolves the { id, price } variant for a row based on its kit selection.
  #variantFor(row) {
    const variants = this.config.variants ?? {};
    if (!this.config.hasKitChoice) return variants.default ?? {};
    const variant = this.#isWithKit(row) ? variants.withKit : variants.withoutKit;
    return variant ?? variants.default ?? {};
  }

  #formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    const currency = this.config.currency;
    if (currency) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
        }).format(amount);
      } catch {
        // Fall back to a plain number if the currency/display is unsupported.
      }
    }
    return amount.toFixed(2);
  }

  // Appends each variant's price to the "with kit" / "without kit" options.
  #decorateKitOptions(row) {
    if (!this.config.hasKitChoice) return;
    const variants = this.config.variants ?? {};
    row.querySelectorAll('[data-toggle-kit] option').forEach((option) => {
      const price = variants[option.value]?.price;
      if (price != null) {
        option.textContent = `${option.textContent} — ${this.#formatMoney(price)}`;
      }
    });
  }

  // Totals every participant's selected variant price and shows it at the top.
  #updateTotal() {
    if (!this.totalAmountEl) return;
    const rows = [...this.participantsEl.querySelectorAll('[data-participant]')];
    const total = rows.reduce(
      (sum, row) => sum + (Number(this.#variantFor(row).price) || 0),
      0,
    );
    this.totalAmountEl.textContent = this.#formatMoney(total);
  }

  #collect() {
    const field = (row, name) => row.querySelector(`[data-field="${name}"]`)?.value.trim() ?? '';

    const participants = [...this.participantsEl.querySelectorAll('[data-participant]')].map((row) => {
      const withKit = this.#isWithKit(row);
      const addressStreet = field(row, 'addressStreet');
      const addressSuburb = field(row, 'addressSuburb');
      const addressPostcode = field(row, 'addressPostcode');
      const addressState = field(row, 'addressState');
      return {
        firstName: field(row, 'firstName'),
        lastName: field(row, 'lastName'),
        dob: field(row, 'dob'),
        gender: field(row, 'gender'),
        // Composed single-line address kept for backwards compatibility with the
        // app proxy, alongside the individual parts.
        address: [addressStreet, addressSuburb, `${addressState} ${addressPostcode}`.trim()]
          .filter(Boolean)
          .join(', '),
        addressStreet,
        addressSuburb,
        addressPostcode,
        addressState,
        withKit,
        kitSize: withKit ? field(row, 'kitSize') : '',
        sockSize: withKit ? field(row, 'sockSize') : '',
        hat: withKit ? field(row, 'hat') : '',
        medicalConditions: field(row, 'medicalConditions'),
        friendGrouping: field(row, 'friendGrouping'),
        activeKidsVoucher: field(row, 'activeKidsVoucher'),
        variantId: String(this.#variantFor(row).id ?? ''),
        productId: String(this.config.productId ?? ''),
      };
    });

    const guardian = (prefix) => ({
      firstName: this.form.elements[`${prefix}_firstName`]?.value.trim() ?? '',
      lastName: this.form.elements[`${prefix}_lastName`]?.value.trim() ?? '',
      email: this.form.elements[`${prefix}_email`]?.value.trim() ?? '',
      phone: this.form.elements[`${prefix}_phone`]?.value.trim() ?? '',
    });

    return {
      agreedTerms: this.form.elements.agreedTerms?.checked === true,
      guardian1: guardian('g1'),
      guardian2: guardian('g2'),
      participants,
    };
  }

  async #handleSubmit(event) {
    event.preventDefault();
    if (!this.form.reportValidity()) return;

    this.#setError('');
    this.#setBusy(true);

    try {
      const payload = this.#collect();
      const res = await fetch(this.dataset.proxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        this.#setError((data.errors ?? [data.error ?? 'Registration failed.']).join('\n'));
        return;
      }

      await this.#addToCart(payload.participants, data.submissionRefs ?? [], data.propertyKey);
      window.location.href = this.dataset.cartUrl || '/cart';
    } catch (error) {
      console.error(error);
      this.#setError('Something went wrong. Please try again.');
    } finally {
      this.#setBusy(false);
    }
  }

  async #addToCart(participants, submissionRefs, propertyKey) {
    const key = propertyKey || '_registration_ref';
    const items = participants.map((p, i) => ({
      id: p.variantId,
      quantity: 1,
      properties: {
        Participant: `${p.firstName} ${p.lastName}`.trim(),
        [key]: submissionRefs[i],
      },
    }));

    const res = await fetch(this.dataset.cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.description || 'Unable to add to cart.');
    }
  }

  #setBusy(busy) {
    if (!this.submitButton) return;
    this.submitButton.disabled = busy;
    this.submitButton.setAttribute('aria-busy', String(busy));
  }

  #setError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
  }
}

if (!customElements.get('pathways-registration')) {
  customElements.define('pathways-registration', PathwaysRegistration);
}
